import Phaser from "phaser";
import { TILE_W, TILE_H } from "@ember/shared";

/** Generate all runtime textures (we ship no art assets for the slice). */
export function generateTextures(scene: Phaser.Scene) {
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  // --- isometric ground diamond (white, tinted per-biome at use site) ---
  g.clear();
  g.fillStyle(0xffffff, 1);
  g.beginPath();
  g.moveTo(TILE_W / 2, 0);
  g.lineTo(TILE_W, TILE_H / 2);
  g.lineTo(TILE_W / 2, TILE_H);
  g.lineTo(0, TILE_H / 2);
  g.closePath();
  g.fillPath();
  // subtle top highlight + bottom shade for depth
  g.fillStyle(0xffffff, 0.12);
  g.beginPath();
  g.moveTo(TILE_W / 2, 0);
  g.lineTo(TILE_W, TILE_H / 2);
  g.lineTo(TILE_W / 2, TILE_H / 2);
  g.closePath();
  g.fillPath();
  // right face (darker — shadow side)
  g.fillStyle(0x000000, 0.18);
  g.beginPath();
  g.moveTo(TILE_W / 2, TILE_H);
  g.lineTo(TILE_W, TILE_H / 2);
  g.lineTo(TILE_W / 2, TILE_H / 2);
  g.closePath();
  g.fillPath();
  g.generateTexture("tile", TILE_W, TILE_H);

  // --- tile variants: same diamond with subtle speckle detail, so the ground
  // isn't a flat checkerboard. White-on-white keeps per-biome tinting intact.
  const speckle = (seed: number, name: string) => {
    g.clear();
    g.fillStyle(0xffffff, 1);
    g.beginPath();
    g.moveTo(TILE_W / 2, 0);
    g.lineTo(TILE_W, TILE_H / 2);
    g.lineTo(TILE_W / 2, TILE_H);
    g.lineTo(0, TILE_H / 2);
    g.closePath();
    g.fillPath();
    g.fillStyle(0xffffff, 0.12);
    g.beginPath();
    g.moveTo(TILE_W / 2, 0);
    g.lineTo(TILE_W, TILE_H / 2);
    g.lineTo(TILE_W / 2, TILE_H / 2);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x000000, 0.18);
    g.beginPath();
    g.moveTo(TILE_W / 2, TILE_H);
    g.lineTo(TILE_W, TILE_H / 2);
    g.lineTo(TILE_W / 2, TILE_H / 2);
    g.closePath();
    g.fillPath();
    // deterministic speckles inside the diamond
    let s = seed;
    const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
    for (let i = 0; i < 9; i++) {
      const u = rnd() - 0.5;
      const v = rnd() - 0.5;
      // keep points inside the diamond (|u| + |v| < 0.5 in normalized space)
      if (Math.abs(u) + Math.abs(v) > 0.42) continue;
      const px = TILE_W / 2 + u * TILE_W;
      const py = TILE_H / 2 + v * TILE_H;
      g.fillStyle(rnd() > 0.5 ? 0xffffff : 0x000000, 0.08 + rnd() * 0.07);
      g.fillCircle(px, py, 1 + rnd() * 1.4);
    }
    g.generateTexture(name, TILE_W, TILE_H);
  };
  speckle(1337, "tileV1");
  speckle(7919, "tileV2");

  // --- grass tuft (white — tinted to a brighter biome green at use site) ---
  g.clear();
  g.lineStyle(2, 0xffffff, 0.9);
  for (const [bx, lean] of [
    [5, -3],
    [9, 1],
    [13, 4],
  ] as const) {
    g.beginPath();
    g.moveTo(bx, 12);
    g.lineTo(bx + lean, 2);
    g.strokePath();
  }
  g.generateTexture("tuft", 18, 14);

  // --- pebbles (grey-ish; tinted at use site) ---
  g.clear();
  g.fillStyle(0xffffff, 0.85);
  g.fillEllipse(5, 8, 7, 5);
  g.fillStyle(0xffffff, 0.6);
  g.fillEllipse(12, 9, 5, 4);
  g.generateTexture("pebble", 18, 13);

  // --- diamond outline (grid / hover) ---
  g.clear();
  g.lineStyle(2, 0xffffff, 1);
  g.beginPath();
  g.moveTo(TILE_W / 2, 1);
  g.lineTo(TILE_W - 1, TILE_H / 2);
  g.lineTo(TILE_W / 2, TILE_H - 1);
  g.lineTo(1, TILE_H / 2);
  g.closePath();
  g.strokePath();
  g.generateTexture("tile_outline", TILE_W, TILE_H);

  // --- soft radial glow (additive light) ---
  const glowSize = 256;
  g.clear();
  const steps = 32;
  for (let i = steps; i > 0; i--) {
    const r = (glowSize / 2) * (i / steps);
    const a = 0.04 * (1 - i / steps) + 0.01;
    const warm = i / steps;
    const r2 = 255;
    const gr = Math.round(180 + warm * 70);
    const b = Math.round(80 + warm * 100);
    const col = (r2 << 16) | (gr << 8) | b;
    g.fillStyle(col, a);
    g.fillCircle(glowSize / 2, glowSize / 2, r);
  }
  g.generateTexture("glow", glowSize, glowSize);

  // --- soft elliptical shadow ---
  g.clear();
  g.fillStyle(0x000000, 0.32);
  g.fillEllipse(24, 10, 40, 16);
  g.generateTexture("shadow", 48, 20);

  // --- selection ring ---
  g.clear();
  g.lineStyle(3, 0xffb066, 0.95);
  g.strokeEllipse(28, 14, 48, 22);
  g.generateTexture("ring", 56, 28);

  // --- isometric wall block: an extruded tile diamond (top + two faces),
  // white with baked shading so it tints per material. Drawn so the base
  // diamond exactly matches the ground-tile footprint.
  {
    const H = 22; // extrusion height in px
    const W = TILE_W;
    const T = TILE_H;
    g.clear();
    // left face (medium shade)
    g.fillStyle(0xffffff, 1);
    g.beginPath();
    g.moveTo(0, T / 2);
    g.lineTo(W / 2, T);
    g.lineTo(W / 2, T + H);
    g.lineTo(0, T / 2 + H);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x000000, 0.28);
    g.beginPath();
    g.moveTo(0, T / 2);
    g.lineTo(W / 2, T);
    g.lineTo(W / 2, T + H);
    g.lineTo(0, T / 2 + H);
    g.closePath();
    g.fillPath();
    // right face (darkest — shadow side, matches ground tile shading)
    g.fillStyle(0xffffff, 1);
    g.beginPath();
    g.moveTo(W / 2, T);
    g.lineTo(W, T / 2);
    g.lineTo(W, T / 2 + H);
    g.lineTo(W / 2, T + H);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x000000, 0.46);
    g.beginPath();
    g.moveTo(W / 2, T);
    g.lineTo(W, T / 2);
    g.lineTo(W, T / 2 + H);
    g.lineTo(W / 2, T + H);
    g.closePath();
    g.fillPath();
    // top face (brightest)
    g.fillStyle(0xffffff, 1);
    g.beginPath();
    g.moveTo(W / 2, 0);
    g.lineTo(W, T / 2);
    g.lineTo(W / 2, T);
    g.lineTo(0, T / 2);
    g.closePath();
    g.fillPath();
    g.fillStyle(0xffffff, 0.14);
    g.beginPath();
    g.moveTo(W / 2, 0);
    g.lineTo(W, T / 2);
    g.lineTo(W / 2, T / 2);
    g.lineTo(0, T / 2);
    g.closePath();
    g.fillPath();
    // plank seams on the faces for a wooden read
    g.lineStyle(1, 0x000000, 0.18);
    for (let i = 1; i <= 2; i++) {
      const yo = (H / 3) * i;
      g.beginPath();
      g.moveTo(0, T / 2 + yo);
      g.lineTo(W / 2, T + yo);
      g.strokePath();
      g.beginPath();
      g.moveTo(W / 2, T + yo);
      g.lineTo(W, T / 2 + yo);
      g.strokePath();
    }
    // crisp silhouette edges
    g.lineStyle(1.5, 0x000000, 0.35);
    g.beginPath();
    g.moveTo(W / 2, 0);
    g.lineTo(W, T / 2);
    g.lineTo(W, T / 2 + H);
    g.lineTo(W / 2, T + H);
    g.lineTo(0, T / 2 + H);
    g.lineTo(0, T / 2);
    g.closePath();
    g.strokePath();
    g.lineStyle(1, 0x000000, 0.25);
    g.beginPath();
    g.moveTo(0, T / 2);
    g.lineTo(W / 2, T);
    g.lineTo(W, T / 2);
    g.strokePath();
    g.beginPath();
    g.moveTo(W / 2, T);
    g.lineTo(W / 2, T + H);
    g.strokePath();
    g.generateTexture("wallBlock", W, T + H);
  }

  // --- puffy cloud: overlapping soft white blobs. Drawn with concentric
  // fading ellipses since Graphics has no blur; reads soft at display scale.
  {
    const cw = 280;
    const ch = 150;
    g.clear();
    const blobs: [number, number, number, number][] = [
      [110, 95, 150, 70],
      [180, 80, 130, 64],
      [70, 80, 100, 56],
      [220, 95, 90, 46],
      [150, 65, 100, 50],
    ];
    for (const [bx, by, bw, bh] of blobs) {
      const layers = 10;
      for (let i = layers; i >= 1; i--) {
        const f = i / layers;
        g.fillStyle(0xffffff, 0.055 + (1 - f) * 0.1);
        g.fillEllipse(bx, by, bw * f, bh * f);
      }
    }
    g.generateTexture("cloud", cw, ch);
  }

  // --- vignette: radial edge darkening for a cinematic frame (screen-fixed).
  // Concentric soft strokes build up alpha toward the edges, leaving the
  // center clear. Stretched over the whole screen at use site.
  const vw = 320;
  const vh = 180;
  g.clear();
  const vSteps = 24;
  for (let i = 0; i < vSteps; i++) {
    const f = i / vSteps;
    const a = Math.pow(f, 2.4) * 0.085;
    g.lineStyle(10, 0x06040c, a);
    g.strokeEllipse(vw / 2, vh / 2, vw * (1.25 - f * 0.45), vh * (1.35 - f * 0.5));
  }
  g.generateTexture("vignette", vw, vh);

  // spark — tiny warm dot for fire particles
  const sparkSize = 8;
  g.clear();
  g.fillStyle(0xffcc44, 1);
  g.fillCircle(sparkSize / 2, sparkSize / 2, 2.5);
  g.fillStyle(0xffffff, 0.6);
  g.fillCircle(sparkSize / 2, sparkSize / 2, 1);
  g.generateTexture("spark", sparkSize, sparkSize);

  g.destroy();
}
